import { ReactComponent as EditIcon } from "../../assets/icons/tables/edit-icon.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { Tooltip } from "@material-tailwind/react";
import { useLocation, useNavigate } from "react-router";
import { useUsers } from "../../store/useUsers";
import { useMemo } from "react";
import { useAuth } from "../../store/useAuth";
import { Constants } from "../../utils/Constants";
import { useAlert } from "../../store/useAlert";
import { KMeanEnum } from "../../utils/enums";
import { useAutoAnimate } from "@formkit/auto-animate/react";

export const UsersTable = () => {
  /** Hooks */
  const navigate = useNavigate();
  const location = useLocation();
  const goToEditUser = (userId: string) =>
    navigate(`/edit-user/${userId}`, {
      state: {
        usersListLocation: `${location.pathname}${location.search}`,
      },
    });
  const { allUsers, allUsersLoading, blockUser, unblockUser } = useUsers();
  const { permanentlyDeleteUser, user } = useAuth();
  const { setAlert } = useAlert();

  const isSuperAdmin = Number(user?.user_role ?? 0) === 7;
  const [bodyParent] = useAutoAnimate<HTMLTableSectionElement>();

  const filteredUsers = useMemo(() => {
    return allUsers?.filter((user) => !user?.pending_delete);
  }, [allUsers]);

  const UserRoles = {
    1: "Admin",
    5: "Store Admin",
    2: "Manager",
    3: "User",
    4: "External Viewer",
    6: "Plastic Viewer",
    7: "Super Admin",
  };

  return (
    <div className="relative overflow-x-auto">
      {allUsersLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
      )}
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium rounded-xl bg-gray-50">
          <tr>
            <th
              scope="col"
              className="hidden min-w-[150px] sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              Profile image
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-3">
              Full name
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              User level
            </th>
            <th
              scope="col"
              className="hidden min-w-[140px] sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              Store Admin
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              Postalcode
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              Platform
            </th>
            <th scope="col" className="px-2 min-w-[160px] sm:px-6 py-1 sm:py-3">
              Total Revenue
            </th>
            <th
              scope="col"
              className="hidden min-w-[160px] sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              Total Donation
            </th>
            <th
              scope="col"
              className="hidden min-w-[160px] sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              Drops volume
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
            >
              Segments
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-3 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody ref={bodyParent}>
          {filteredUsers?.map((user) => (
            <tr
              key={user?._id}
              className={`${user?.pending_delete ? "bg-red-200" : "bg-white"} ${
                user?.pending_delete ? "hover:bg-red-300" : "hover:bg-gray-100"
              } cursor-pointer transition ease-linear border-b`}
            >
              <th
                onClick={() => goToEditUser(user?._id as string)}
                scope="row"
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3 font-bold text-primary-base whitespace-nowrap"
              >
                <img
                  alt="profile-pic"
                  className="w-[40px] h-[40p] mr-3 rounded-full cursor-pointer"
                  src={
                    user?.profileImage ??
                    require("../../assets/pictures/dummy/dummy-avatar.png")
                  }
                />
              </th>
              <th
                onClick={() => goToEditUser(user?._id as string)}
                scope="row"
                className="px-2 sm:px-6 py-1 sm:py-3 font-bold text-primary-base whitespace-nowrap"
              >
                {user?.first_name} {user?.last_name}{" "}
                <p className="text-red-900">
                  {user?.pending_delete && "(Pending delete)"}
                </p>
              </th>
              <td
                onClick={() => goToEditUser(user?._id as string)}
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
              >
                <div
                  className={`py-[12px] text-center px-[5px] rounded ${
                    user?.user_role === 1 && "bg-red-light"
                  }  ${
                    user?.user_role === 5 && "bg-light-blue-400"
                  } ${user?.user_role === 2 && "bg-primary-light"} ${
                    (user?.user_role === 3 || user?.user_role === 4) &&
                    "bg-blue-gray-300"
                  } ${user?.user_role === 6 && "bg-green-400"} ${
                    user?.user_role === 7 && "bg-purple-600"
                  } flex flex-row items-center justify-center  text-white font-satoshi font-bold w-[80px] text-xs`}
                >
                  {/* @ts-ignore */}
                  {UserRoles[Number(user?.user_role ?? 3)]}
                </div>
              </td>
             <td
  onClick={() => goToEditUser(user?._id as string)}
  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
>
  {Number(user?.user_role ?? 0) === 5 ? "Yes" : "No"}
</td>
<td
  onClick={() => goToEditUser(user?._id as string)}
  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
>
  {user?.postal_code}
</td>
              <td
                onClick={() => goToEditUser(user?._id as string)}
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
              >
                {user?.platform === "ios" && (
                  <img
                    className="w-[30px] h-[30px]"
                    alt="ios-logo"
                    src={require("../../assets/icons/platforms/apple.png")}
                  />
                )}
                {user?.platform === "android" && (
                  <img
                    className="w-[30px] h-[30px]"
                    alt="android-logo"
                    src={require("../../assets/icons/platforms/android.png")}
                  />
                )}
              </td>
              <td
                onClick={() => goToEditUser(user?._id as string)}
                className="px-2 sm:px-6 py-1 sm:py-3"
              >
                {Number(
                  user?.totalRevenue ? user?.totalRevenue / 100 : 0
                )?.toFixed(2)}
                €
              </td>
              <td
                onClick={() => goToEditUser(user?._id as string)}
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
              >
                {Number(
                  user?.amountDonated ? user?.amountDonated / 100 : 0
                )?.toFixed(2)}
                €
              </td>
              <td
                onClick={() => goToEditUser(user?._id as string)}
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
              >
                {Number(user?.dropsVolume ?? 0) +
                  Number(user?.beevisionDropsVolume ?? 0) +
                  Number(user?.tomraDropsVolume ?? 0)}
              </td>
              <td
                onClick={() => goToEditUser(user?._id as string)}
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-3"
              >
                {/* @ts-ignore */}
                {KMeanEnum?.[user?.kMeans] ?? "-"}
              </td>
              <td className="px-2 sm:px-6 py-1 sm:py-3 flex items-center justify-end cursor-pointer">
                <Tooltip content="Edit">
                  <EditIcon
                    onClick={() => goToEditUser(user?._id as string)}
                    className="mr-3 sm:mr-7"
                  />
                </Tooltip>
                {isSuperAdmin ? (
                  <Tooltip content={user?.isBlocked ? "Unblock" : "Block"}>
                    <button
                      type="button"
                      onClick={() => {
                        if (user?.isBlocked) {
                          const confirmUnblock = window.confirm(
                            `Are you sure you want to unblock ${user?.first_name} ${user?.last_name}?`
                          );
                          if (confirmUnblock) {
                            unblockUser(user?._id as string);
                          }
                        } else {
                          const confirmBlock = window.confirm(
                            `Are you sure you want to block ${user?.first_name} ${user?.last_name}?`
                          );
                          if (confirmBlock) {
                            blockUser(user?._id as string);
                          }
                        }
                      }}
                      className={`mr-3 sm:mr-7 px-3 py-1 rounded-md font-satoshi font-bold text-xs text-white transition ease-linear ${
                        user?.isBlocked
                          ? "bg-primary-base hover:opacity-90"
                          : "bg-red-400 hover:bg-red-300"
                      }`}
                    >
                      {user?.isBlocked ? "Unblock" : "Block"}
                    </button>
                  </Tooltip>
                ) : null}
                <Tooltip content="Delete">
                  <TrashIcon
                    onClick={() => {
                      if (user?.phone_number === Constants.TEST_PHONE_NUMBER) {
                        return setAlert({
                          isOpen: true,
                          message: "You cannot delete this user",
                          severity: "error",
                        });
                      }
                      // A sub-cent remainder (e.g. 0.7, from drops priced before
                      // they were rounded) can't be paid out, so it isn't a balance.
                      const hasBalance = Number(user?.balance ?? 0) >= 1;
                      const confirmDelete = window.confirm(
                        hasBalance
                          ? `This user's balance is not zero (${(
                              Number(user?.balance ?? 0) / 100
                            ).toFixed(
                              2
                            )}€). Deleting is permanent and their balance will be lost. Are you sure you want to permanently remove this user?`
                          : "Are you sure you want to permanently remove this user?"
                      );
                      if (!confirmDelete) return;
                      // Recorded on the closure record, so a deletion can be
                      // accounted for later. Required, not optional.
                      // eslint-disable-next-line no-alert
                      const reason = window.prompt(
                        "Why is this account being removed? This is stored with the deletion record."
                      );
                      if (!reason?.trim()) {
                        return setAlert({
                          isOpen: true,
                          message: "A reason is required to remove an account.",
                          severity: "error",
                        });
                      }
                      permanentlyDeleteUser(user?._id as string, reason.trim());
                    }}
                  />
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
